/**
 * Chart Configuration for Sales Timeline
 *
 * Constants, colour palettes, and styling configuration
 * for the WeekChart and related timeline chart components.
 *
 * US-PE-006: Sales Timeline - Phase 2 Chart Components
 */

/**
 * Strain colour palette for stacked bar charts
 * These colours are chosen for:
 * - Visual distinction between adjacent bars
 * - Accessibility (sufficient contrast)
 * - Alignment with Odin design system where possible
 */
export const STRAIN_COLORS = {
  /**
   * Default colour palette for strains
   * Used when strain-specific colours aren't defined
   */
  default: [
    '#3b82f6', // blue-500
    '#8b5cf6', // violet-500
    '#ec4899', // pink-500
    '#f59e0b', // amber-500
    '#10b981', // emerald-500
    '#06b6d4', // cyan-500
    '#f97316', // orange-500
    '#6366f1', // indigo-500
    '#14b8a6', // teal-500
    '#a855f7', // purple-500
  ],

  /**
   * Get a consistent colour for a strain based on its position in the list
   *
   * @param {string} strainId - The strain ID
   * @param {Array} strains - Array of strain objects with id property
   * @returns {string} Hex colour code
   */
  getStrainColor: (strainId, strains) => {
    if (!strains || strains.length === 0) {
      return STRAIN_COLORS.default[0]
    }

    const index = strains.findIndex(s => s.id === strainId)
    if (index === -1) {
      return STRAIN_COLORS.default[0]
    }

    return STRAIN_COLORS.default[index % STRAIN_COLORS.default.length]
  },

  /**
   * Assign colours to an array of strains
   *
   * @param {Array} strains - Array of strain objects
   * @returns {Array} Strains with color property added
   */
  assignColors: (strains) => {
    if (!strains || strains.length === 0) return []

    return strains.map((strain, index) => ({
      ...strain,
      color: STRAIN_COLORS.default[index % STRAIN_COLORS.default.length]
    }))
  }
}

/**
 * Chart styling configuration
 * Defines consistent appearance for all chart elements
 */
export const CHART_CONFIG = {
  /**
   * Demand line styling
   * Dark slate colour for clear visibility against coloured bars
   */
  demandLine: {
    stroke: '#1e293b', // slate-800
    strokeWidth: 2,
    strokeDasharray: undefined, // solid line
    dot: {
      fill: '#1e293b',
      stroke: '#ffffff',
      strokeWidth: 1,
      r: 4
    },
    activeDot: {
      fill: '#1e293b',
      stroke: '#ffffff',
      strokeWidth: 2,
      r: 6
    }
  },

  /**
   * Supply bar styling
   * Rounded top corners for modern appearance
   */
  supplyBar: {
    radius: [4, 4, 0, 0], // [top-left, top-right, bottom-right, bottom-left]
    maxBarSize: 40,
    // Note: fill colour comes from strain configuration
  },

  /**
   * Grid line styling
   * Subtle dashed lines for reference without visual clutter
   */
  grid: {
    strokeDasharray: '3 3',
    stroke: '#e2e8f0', // slate-200
    horizontal: true,
    vertical: false
  },

  /**
   * Axis styling
   * Clean, readable labels
   */
  axis: {
    fontSize: 12,
    fontFamily: 'inherit',
    fill: '#64748b', // slate-500
    tickLine: false,
    axisLine: { stroke: '#e2e8f0' } // slate-200
  },

  /**
   * Tooltip styling (used as reference, actual styling in ChartTooltip)
   */
  tooltip: {
    backgroundColor: '#ffffff',
    borderColor: '#e2e8f0',
    borderRadius: 8,
    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06)'
  },

  /**
   * Default chart dimensions
   */
  dimensions: {
    height: 200,
    minHeight: 150,
    maxHeight: 400
  },

  /**
   * Margins for the chart area
   */
  margin: {
    top: 10,
    right: 10,
    bottom: 20,
    left: 40
  }
}

/**
 * Day labels for X-axis
 * Short format for compact display
 */
export const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/**
 * Full day labels for tooltips and accessibility
 */
export const DAY_LABELS_FULL = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
]

/**
 * Get short day label from date
 *
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Short day label (Mon, Tue, etc.)
 */
export function getDayLabel(date) {
  const d = typeof date === 'string' ? new Date(date) : date
  const dayIndex = d.getDay()
  // getDay() returns 0 for Sunday, so we need to adjust
  const adjustedIndex = dayIndex === 0 ? 6 : dayIndex - 1
  return DAY_LABELS[adjustedIndex]
}

/**
 * Get full day label from date
 *
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Full day label (Monday, Tuesday, etc.)
 */
export function getDayLabelFull(date) {
  const d = typeof date === 'string' ? new Date(date) : date
  const dayIndex = d.getDay()
  const adjustedIndex = dayIndex === 0 ? 6 : dayIndex - 1
  return DAY_LABELS_FULL[adjustedIndex]
}

/**
 * Get the day number from a date
 *
 * @param {Date|string} date - Date object or ISO string
 * @returns {number} Day of month (1-31)
 */
export function getDateNumber(date) {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.getDate()
}

/**
 * Format a date for display in chart labels
 *
 * @param {string} dateString - ISO date string (YYYY-MM-DD)
 * @returns {string} Formatted date (e.g., "6 Jan")
 */
export function formatChartDate(dateString) {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short'
  })
}

/**
 * Format a week range for display
 *
 * @param {string} startDate - ISO date string for week start
 * @param {string} endDate - ISO date string for week end
 * @returns {string} Formatted range (e.g., "6 - 12 Jan 2026")
 */
export function formatWeekRange(startDate, endDate) {
  const start = new Date(startDate)
  const end = new Date(endDate)

  const startDay = start.getDate()
  const endDay = end.getDate()
  const startMonth = start.toLocaleDateString('en-GB', { month: 'short' })
  const endMonth = end.toLocaleDateString('en-GB', { month: 'short' })
  const year = end.getFullYear()

  // Same month
  if (startMonth === endMonth) {
    return `${startDay} - ${endDay} ${endMonth} ${year}`
  }

  // Different months
  return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`
}

export default {
  STRAIN_COLORS,
  CHART_CONFIG,
  DAY_LABELS,
  DAY_LABELS_FULL,
  getDayLabel,
  getDayLabelFull,
  getDateNumber,
  formatChartDate,
  formatWeekRange
}
